import moment from "moment-timezone";
import React from "react";
import styled from "styled-components";

import { LiveIcon } from "src/static/icons";

interface ShiftCardProps {
  shiftName: string;
  startTime: string;
  endTime: string;
  shiftLocation: string;
  registeredUsers?: number;
  maxUsers?: number;
  onClick: () => void;
}

const ShiftCard: React.FC<ShiftCardProps> = ({
  shiftName,
  startTime,
  endTime,
  shiftLocation,
  onClick,
}) => {
  const formatTime = (time: string) =>
    moment(time).tz("America/New_York").format("h:mm A");

  const startTimeFormatted = formatTime(startTime);
  const endTimeFormatted = formatTime(endTime);
  const startDayOfWeek = moment(startTime)
    .tz("America/New_York")
    .format("ddd")
    .toUpperCase();
  const endDayOfWeek = moment(endTime)
    .tz("America/New_York")
    .format("ddd")
    .toUpperCase();

  const rangeLabel =
    startDayOfWeek === endDayOfWeek
      ? `${startDayOfWeek} ${startTimeFormatted} - ${endTimeFormatted}`
      : `${startDayOfWeek} ${startTimeFormatted} - ${endDayOfWeek} ${endTimeFormatted}`;

  const now = moment().tz("America/New_York");
  const isLive = now.isBetween(startTime, endTime);
  const isPast = now.isAfter(endTime);

  return (
    <Card onClick={onClick} $isPast={isPast}>
      <Header>
        <TitleContainer>
          {isLive && <LiveIcon />}
          <ShiftName>{shiftName}</ShiftName>
        </TitleContainer>
      </Header>
      <InfoContainer>
        <InfoValue>{rangeLabel}</InfoValue>
        <InfoValue>{shiftLocation}</InfoValue>
      </InfoContainer>
    </Card>
  );
};

const Card = styled.div<{ $isPast: boolean }>`
  display: flex;
  width: 100%;
  max-width: 371px;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid var(--North-Border-Gray, #d1d5db);
  background: ${({ $isPast }) =>
    $isPast ? "#f9fafb" : "var(--North-Background-White, #fff)"};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9fafb;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  max-width: 50%;
  align-items: center;
  gap: 8px;
`;

const ShiftName = styled.h2`
  color: var(--North-Text-Body, #1f2937);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const InfoValue = styled.p`
  color: var(--North-Text-Body, #1f2937);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin: 0;
`;

export default ShiftCard;
